import { components } from "@/api-schema";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useAppRoleGroupSearchQuery } from "@/common/api/app-role/use-app-role-group-search.query";
import { useAppRoleSyncMutation } from "@/common/api/app-role/use-app-role-sync.mutation";
import { useAppRoleUpdateMutation } from "@/common/api/app-role/use-app-role-update.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { AppRoleFormSchemaType, createAppRoleFormSchema } from "../_helpers/app-role-form.helpers";
import { AppRoleFormFields } from "./app-role-form-fields";

type AppRole = components["schemas"]["AppRole"];

export interface AppRoleUpdateFormProps {
  formId: string;
  appRole: AppRole;
  applicationUuid: string;
  appRoleUuid: string;
  onSaveDisabledChange: (disabled: boolean) => void;
}

export function AppRoleUpdateForm({
  formId,
  appRole,
  applicationUuid,
  appRoleUuid,
  onSaveDisabledChange,
}: AppRoleUpdateFormProps) {
  const { t } = useTranslation(["ram", "common"]);
  const [_, navigate] = useLocation();
  const { isPending: isUpdatePending, mutateAsync: updateMutateAsync } = useAppRoleUpdateMutation();
  const { isPending: isSyncPending, mutateAsync: syncMutateAsync } = useAppRoleSyncMutation();
  const isPending = isUpdatePending || isSyncPending;
  const isV1ManagedAppRole = appRole.v1Id !== null && appRole.v1Id !== undefined;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data: pageData, isLoading } = useAppRoleGroupSearchQuery({
    options: {
      parameters: { search: searchTerm, page, pageSize },
      body: { appRole: appRoleUuid },
    },
  });

  const form = useForm<AppRoleFormSchemaType>({
    resolver: zodResolver(createAppRoleFormSchema({ isNameDisabled: isV1ManagedAppRole })),
    mode: "onChange",
    defaultValues: { name: appRole.name },
  });

  const handlePaginationChange = useCallback(({ page, pageSize }: { page: number; pageSize: number }) => {
    setPage(page);
    setPageSize(pageSize);
  }, []);

  const handleSaveDisabledChange = useCallback(
    (childDisabled: boolean) => {
      onSaveDisabledChange(childDisabled || isPending);
    },
    [onSaveDisabledChange, isPending],
  );

  const handleSave = async (groupUuidsToAdd: string[], groupUuidsToRemove: string[]): Promise<void> => {
    const isValid = await form.trigger();

    if (!isValid) return;

    const nameValue = form.getValues("name");
    const shouldUpdateName = nameValue !== appRole.name;

    await toastMutationStatus({
      mutationFn: async () => {
        if (!isV1ManagedAppRole && form.formState.isDirty && shouldUpdateName) {
          await updateMutateAsync({ uuid: appRoleUuid, name: nameValue });
        }
        if (groupUuidsToAdd.length > 0 || groupUuidsToRemove.length > 0) {
          await syncMutateAsync({
            appRole: appRoleUuid,
            groupsToAdd: groupUuidsToAdd,
            groupsToRemove: groupUuidsToRemove,
          });
        }
      },
      successMessage: t("ram:PermissionHasBeenUpdated"),
      errorMessage: t("ram:ErrorInUpdatingThePermission"),
    });

    navigate(`~/ram/application/${applicationUuid}/app-role`);
  };

  return (
    <Form {...form}>
      <AppRoleFormFields
        formId={formId}
        form={form}
        pageData={pageData}
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        onSearchChange={setSearchTerm}
        onPaginationChange={handlePaginationChange}
        onSave={handleSave}
        isNameDisabled={isV1ManagedAppRole}
        onSaveDisabledChange={handleSaveDisabledChange}
      />
    </Form>
  );
}
